/**
 * Email sign-in-code state machine for native and desktop shells.
 *
 * The shell cannot use a clickable link. Office staff read email on a phone, so
 * the link opens there rather than on the desktop that is waiting; and mail
 * scanners prefetch URLs, redeeming a single-use credential before the human
 * clicks. A typed code is immune to both. See
 * `docs/plans/2026-08-04-feat-desktop-signin-methods-plan.md`.
 *
 * Shaped like `use-password-sign-in.ts` — same `MfaStepUpController` surface —
 * so `login.tsx` selects ONE step-up controller by source rather than wiring
 * three half-overlapping ones.
 *
 * Two measured facts drive the design (probed against a live stack, recorded in
 * the plan; do not re-derive):
 *
 *  1. `verifyOtp({ type: 'email' })` mints an **aal1** session with
 *     `amr: ["otp"]`. A TOTP-enrolled user is therefore NOT stepped up by the
 *     code alone, so the strict check below is the only thing between a typed
 *     code and the app — exactly as on the password and passkey paths.
 *  2. The request endpoint spends BOTH its rate limits before it looks the
 *     account up. That is what makes surfacing a 429 safe here: it is returned
 *     for an address with no account just as readily, so it separates "asked
 *     too often" from "asked recently", never "exists" from "does not".
 *
 * Every other outcome is deliberately indistinguishable. An unknown address
 * advances to the code step like any other and fails there as
 * `invalid_or_expired`, because an address that stopped early would answer the
 * question the generic envelope exists to refuse.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MFA_VERIFY_ERROR_KEYS, classifyMfaVerifyError } from '@/components/auth/mfa-verify-error';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { checkMFARequirementStrict, isLockContentionError, verifyMFACode } from '@/lib/auth-utils';

import type { MfaStepUpController } from '@/components/auth/use-passkey-sign-in';

/** Which half of the flow the surface should render. */
export type EmailCodeStep = 'address' | 'code';

/**
 * Advisory state, all non-toast: every one of these leaves the user on a form
 * they can act on, and a toast over a form is a worse place to put the reason.
 *
 * `invalid_or_expired` is ONE state on purpose. The SDK and the server cannot
 * tell a wrong code from an expired one — `otp_expired` covers both, and the
 * same server branch validates mismatch and expiry — so offering two messages
 * would be inventing a distinction the system does not have.
 */
export type EmailCodeNotice = 'none' | 'invalid_or_expired' | 'rate_limited' | 'check_failed';

export interface UseEmailCodeSignInOptions {
  /** Called once the code (and TOTP step-up, if owed) completes. */
  readonly onSignedIn: (userId: string) => void | Promise<void>;
  /** Optional context-tagged error log. Defaults to no-op. */
  readonly logError?: (label: string, error: unknown) => void;
}

export interface UseEmailCodeSignInResult extends MfaStepUpController {
  readonly step: EmailCodeStep;
  readonly notice: EmailCodeNotice;
  /** The address a code was requested for, for the "we sent it to…" line. */
  readonly pendingEmail: string;
  readonly sendCode: (email: string) => Promise<void>;
  readonly verifyCode: (code: string) => Promise<void>;
  /** Re-runs a step-up check that could not answer. Needs no new code. */
  readonly retryStepUpCheck: () => Promise<void>;
  readonly clearNotice: () => void;
  /**
   * Back to the address step, for "use a different email". Revokes a session
   * still owing a step-up: `check_failed` reaches this control with one live.
   */
  readonly restart: () => void;
}

const REQUEST_PATH = '/functions/v1/send-magic-link-otp';

/** The wire name the edge function accepts. Changing it changes the contract. */
const CODE_DELIVERY_MODE = 'code';

export function useEmailCodeSignIn(options: UseEmailCodeSignInOptions): UseEmailCodeSignInResult {
  const [step, setStep] = useState<EmailCodeStep>('address');
  const [isLoading, setIsLoading] = useState(false);
  const [showMFAInput, setShowMFAInput] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [signedInUserId, setSignedInUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<EmailCodeNotice>('none');
  const [isSettling, setIsSettling] = useState(false);
  const inFlightRef = useRef(false);
  const attemptRef = useRef(0);
  const { toast } = useToast();
  const { t } = useTranslation('auth');
  /**
   * True from the moment a session is persisted until the step-up over it is
   * discharged. A ref, not state: every consumer reads it from an async
   * continuation, where a render-scoped boolean is already a render stale.
   */
  const stepUpOwedRef = useRef(false);

  const resolveStepUp = async (userId: string, attempt: number): Promise<void> => {
    let requirement: Awaited<ReturnType<typeof checkMFARequirementStrict>>;
    try {
      requirement = await checkMFARequirementStrict();
    } catch (error) {
      if (attempt !== attemptRef.current) {
        return;
      }
      // A THROWN check gets the same fail-closed answer as an indeterminate
      // one, and needs its own handler because both entry points reach here
      // over a session that is already persisted: without this the rejection
      // escapes, the spinner never clears, and the aal1 session sits behind a
      // dead screen with no retry and no way out.
      //
      // Lock contention is the SDK talking to itself, not a fault worth a line.
      if (!isLockContentionError(error)) {
        options.logError?.('emailCodeStepUp', error);
      }
      stepUpOwedRef.current = true;
      setNotice('check_failed');
      setIsLoading(false);
      return;
    }

    if (attempt !== attemptRef.current) {
      return;
    }

    if (requirement.status === 'indeterminate') {
      // FAIL CLOSED. The lossy `checkMFARequirement()` answers `needsMFA: false`
      // on every error path, so proceeding would walk a TOTP-enrolled user into
      // the app un-stepped-up over a session already persisted. The session is
      // KEPT so the retry re-reads the level without spending a second code.
      // The route guard reads the lossy variant and would admit a direct
      // navigation meanwhile — that residual is ADR 0068's, not this block's.
      stepUpOwedRef.current = true;
      setNotice('check_failed');
      setIsLoading(false);
      return;
    }

    if (requirement.status === 'required') {
      stepUpOwedRef.current = true;
      setMfaFactorId(requirement.factorId);
      setShowMFAInput(true);
      setIsLoading(false);
      return;
    }

    // Discharged BEFORE `onSignedIn`, so a failure inside it cannot leave a
    // finished session looking owed to a later teardown. A `finally` rather
    // than a `catch` for the same reason: the check has already answered that
    // nothing is owed, so a failure to redirect is not grounds to revoke a
    // session the user legitimately holds - it clears the spinner and lets the
    // caller surface the fault.
    stepUpOwedRef.current = false;
    try {
      await options.onSignedIn(userId);
    } finally {
      setIsLoading(false);
    }
  };

  const sendCode = async (email: string, attempt: number): Promise<void> => {
    setIsLoading(true);
    setNotice('none');
    const trimmed = email.trim();

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}${REQUEST_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, deliveryMode: CODE_DELIVERY_MODE }),
      });

      if (attempt !== attemptRef.current) {
        return;
      }

      if (response.status === 429) {
        // Safe to surface: both limits are spent before the account lookup, so
        // an address with no account is throttled identically.
        setNotice('rate_limited');
        return;
      }
      if (!response.ok) {
        // Deliberately NOT surfaced as a failure. Anything else here is either a
        // server fault or a refusal that would distinguish one address from
        // another; both advance, and an address that has no code waiting fails
        // at the next step like any wrong code. Status only — the body can
        // carry the address back.
        options.logError?.('emailCodeRequest', { status: response.status });
      }
      setPendingEmail(trimmed);
      setStep('code');
    } catch (error) {
      if (attempt !== attemptRef.current) {
        return;
      }
      // A transport failure is the one case the user can act on directly, and
      // it says nothing about the address.
      options.logError?.('emailCodeRequestTransport', {
        name: error instanceof Error ? error.name : 'unknown',
      });
      setPendingEmail(trimmed);
      setStep('code');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code: string, attempt: number): Promise<void> => {
    setIsLoading(true);
    setNotice('none');

    let userId: string;
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: code.trim(),
        type: 'email',
      });
      if (error !== null || !data.user) {
        if (attempt !== attemptRef.current) {
          return;
        }
        setNotice('invalid_or_expired');
        setIsLoading(false);
        return;
      }
      userId = data.user.id;
    } catch (error) {
      if (attempt !== attemptRef.current) {
        return;
      }
      options.logError?.('emailCodeVerify', {
        name: error instanceof Error ? error.name : 'unknown',
      });
      setNotice('invalid_or_expired');
      setIsLoading(false);
      return;
    }

    // A session exists from here, so the obligation is armed before anything
    // that can fail: a teardown racing the check must find it owed.
    stepUpOwedRef.current = true;
    if (attempt !== attemptRef.current) {
      return;
    }
    setSignedInUserId(userId);
    await resolveStepUp(userId, attempt);
  };

  const retryStepUpCheck = async (attempt: number): Promise<void> => {
    if (signedInUserId === null) {
      return;
    }
    setNotice('none');
    setIsLoading(true);
    await resolveStepUp(signedInUserId, attempt);
  };

  const verifyMFA = async (code: string, attempt: number): Promise<void> => {
    if (mfaFactorId === null) {
      return;
    }
    // The verifier owns the obligation until it settles. Cancellation re-arms
    // it even on success so an abandoned attempt never completes sign-in.
    stepUpOwedRef.current = false;
    setIsLoading(true);
    // `finally`, because a rejecting `onSignedIn` would otherwise strand the
    // user on a permanently disabled step-up screen - the same latch
    // `resolveStepUp` guards against. `verifyMFACode` catches everything and always
    // returns a result, so the ONLY rejection reaching here is `onSignedIn`'s,
    // and that path has already made the session aal2 and complete. If that
    // ever stops being true, this needs a `catch` that re-arms.
    try {
      const result = await verifyMFACode(mfaFactorId, code);
      if (attempt !== attemptRef.current) {
        stepUpOwedRef.current = true;
        return;
      }
      if (result.success) {
        if (signedInUserId !== null) {
          await options.onSignedIn(signedInUserId);
        }
        return;
      }
      // Hand the obligation back: the step-up view stays up over the same aal1
      // session, so an exit from here still owes a teardown.
      stepUpOwedRef.current = true;
      toast({
        title: t('passwordSignIn.verificationFailedTitle'),
        description: t(MFA_VERIFY_ERROR_KEYS[classifyMfaVerifyError(result.error)]),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const teardownSession = async (label: string): Promise<void> => {
    // Both channels, then local scope. `signOut()` reports a failed revoke on
    // the RESOLVED channel and rejects only when the call throws, so a
    // try/catch alone reads a server failure as success — and on that path the
    // SDK returns before removing its own copy.
    try {
      // Scope local: this teardown revokes the abandoned step-up session
      // only - global would revoke every device over a dismissed prompt.
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error === null) {
        return;
      }
      options.logError?.(label, { name: error.name, code: error.code });
    } catch (error) {
      options.logError?.(label, {
        name: error instanceof Error ? error.name : 'unknown',
      });
    }
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      options.logError?.(`${label}Local`, {
        name: error instanceof Error ? error.name : 'unknown',
      });
    }
  };

  const resetToAddress = (): void => {
    setSignedInUserId(null);
    setShowMFAInput(false);
    setMfaFactorId(null);
    setNotice('none');
    setStep('address');
    setPendingEmail('');
    setIsLoading(false);
  };

  const runAttempt = async (action: (attempt: number) => Promise<void>): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsSettling(true);
    const attempt = attemptRef.current;
    try {
      await action(attempt);
    } finally {
      // A stale SDK response can persist a session. Keep every sign-in method
      // disabled until its local revocation settles, before a new attempt owns it.
      if (attempt !== attemptRef.current && stepUpOwedRef.current) {
        stepUpOwedRef.current = false;
        await teardownSession('emailCodeAbandon');
      }
      inFlightRef.current = false;
      setIsLoading(false);
      setIsSettling(false);
    }
  };

  const abandon = (label: string): void => {
    attemptRef.current += 1;
    if (!inFlightRef.current && stepUpOwedRef.current) {
      stepUpOwedRef.current = false;
      void runAttempt(async () => await teardownSession(label));
    }
  };

  /**
   * Every way back to the address step, and the reason there is only one of
   * them: `check_failed` leaves a MINTED session on screen with the code step
   * still showing, so "use a different email" is a live-session exit exactly
   * like Back out of the step-up view. Resetting state alone would clear the
   * obligation and leave that aal1 session in storage.
   *
   * UI state resets first so neither exit blocks on the network; the teardown
   * continues regardless.
   */
  const abandonAndReset = (label: string): void => {
    abandon(label);
    resetToAddress();
  };

  const abandonSignIn = (): void => {
    abandon('emailCodeAbandon');
  };

  return {
    step,
    isLoading: isLoading || isSettling,
    showMFAInput,
    userEmail: pendingEmail,
    notice,
    pendingEmail,
    sendCode: async (email) => await runAttempt(async (attempt) => await sendCode(email, attempt)),
    verifyCode: async (code) =>
      await runAttempt(async (attempt) => await verifyCode(code, attempt)),
    verifyMFA: async (code) => await runAttempt(async (attempt) => await verifyMFA(code, attempt)),
    retryStepUpCheck: async () => await runAttempt(retryStepUpCheck),
    clearNotice: () => setNotice('none'),
    restart: () => abandonAndReset('emailCodeRestart'),
    dismissMFA: () => abandonAndReset('emailCodeDismiss'),
    abandonSignIn,
  };
}
